import { Inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AGENT_GATEWAY_AUTH, AgentGatewayAuth } from './agent-gateway-auth';
import { parseQuotaBucket, parseTopUsersToday } from './token-quota.util';
import {
  AgentAnswerSource,
  AgentGatewayCardPreview,
  AgentGatewayRequest,
  AgentGatewayResponseBody,
  AgentGatewayResult,
  AgentGatewayStatusEvent,
  AgentGatewayStreamHandlers,
  ChartSpec,
  ChartType,
  ConversationScope,
  CopilotLiveDataBadge,
  CopilotPersona,
  HelpLink,
  NavAction,
  ReportLink,
  ShortFormatCard,
  CoachingSuggestion,
  DecisionResponse,
  PersonAction,
  RecommendationActionKind,
  RecommendationCard,
  TableColumn,
  TableColumnType,
  TableSort,
  TableSpec,
  TokenUsage,
  TokenQuotaUsage,
  ToolCheck,
  ToolStep,
  UsageChip
} from './copilot.types';

/** Abort the request if the gateway does not respond within this window. */
const AGENT_GATEWAY_TIMEOUT_MS = 20_000;

/** Streaming can legitimately run longer than a single request (tool calls, live data). */
const AGENT_GATEWAY_STREAM_TIMEOUT_MS = 60_000;

/**
 * Thin client for the NestJS Agent Gateway (POST /agent/chat). Read-only: it only
 * sends a message + context and returns a normalized answer. It never throws — every
 * failure is mapped to a typed {@link AgentGatewayResult} the facade can render.
 */
@Injectable({ providedIn: 'root' })
export class AgentGatewayService {
  constructor(
    @Inject(AGENT_GATEWAY_AUTH) private readonly auth: AgentGatewayAuth,
  ) {}

  get isConfigured(): boolean {
    return !!environment.agentGatewayBaseUrl;
  }

  async sendMessage(request: AgentGatewayRequest): Promise<AgentGatewayResult> {
    const base = this.base();
    if (!base) {
      return { status: 'error', errorKind: 'unavailable' };
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), AGENT_GATEWAY_TIMEOUT_MS);

    const headers = this.withIdempotencyKey(
      await this.buildHeaders(request),
      request,
    );
    const authErr = this.requireAuthHeaders(headers);
    if (authErr) {
      clearTimeout(timer);
      return authErr;
    }

    let response: Response;
    try {
      response = await fetch(`${base}/agent/chat`, {
        method: 'POST',
        headers,
        body: this.requestBody(request),
        signal: controller.signal,
      });
    } catch (err) {
      return {
        status: 'error',
        errorKind: this.isAbort(err) ? 'timeout' : 'unavailable',
      };
    } finally {
      clearTimeout(timer);
    }

    // Auth failures: session expired or role not permitted.
    if (response.status === 401 || response.status === 403) {
      return { status: 'error', errorKind: 'unauthorized' };
    }
    // Any other non-2xx (404/408/429/5xx or gateway down) is "unavailable" to the
    // user, enriched with the gateway's uniform error envelope when present.
    if (!response.ok) {
      return this.errorFromEnvelope(response);
    }

    const body = (await response.json().catch(() => null)) as AgentGatewayResponseBody | null;
    return this.toResult(body);
  }

  /**
   * Soft daily/monthly token quota for the signed-in user (Usage settings).
   * Never throws — returns null on any transport or auth failure.
   */
  async getTokenQuotaUsage(pricingPlan?: string): Promise<TokenQuotaUsage | null> {
    const base = this.base();
    if (!base) {
      return null;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), AGENT_GATEWAY_TIMEOUT_MS);

    const params = new URLSearchParams();
    const plan = pricingPlan?.trim();
    if (plan) {
      params.set('pricingPlan', plan);
    }
    const qs = params.toString();
    const url = `${base}/agent/usage${qs ? `?${qs}` : ''}`;

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'GET',
        headers: await this.buildHeaders({ message: '', context: {} }),
        signal: controller.signal,
      });
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }

    if (response.status === 401 || response.status === 403 || !response.ok) {
      return null;
    }

    const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    if (!body?.daily || !body?.monthly) {
      return null;
    }
    const daily = parseQuotaBucket(body.daily);
    const monthly = parseQuotaBucket(body.monthly);
    if (!daily || !monthly) {
      return null;
    }
    return {
      daily,
      monthly,
      topUsersToday: parseTopUsersToday(body.topUsersToday),
    };
  }

  /**
   * Submit helpful / not helpful feedback for an assistant answer.
   * Never throws — returns false on transport or auth failure.
   */
  async submitFeedback(input: {
    rating: 'helpful' | 'not_helpful';
    conversationId: string;
    messageId: string;
    requestId: string;
    recommendationType?: string;
    clickedAction?: string;
    comment?: string;
  }): Promise<boolean> {
    const base = this.base();
    if (!base) {
      return false;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), AGENT_GATEWAY_TIMEOUT_MS);

    let response: Response;
    try {
      response = await fetch(`${base}/agent/feedback`, {
        method: 'POST',
        headers: await this.buildHeaders({ message: '', context: {} }),
        body: JSON.stringify(input),
        signal: controller.signal,
      });
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }

    if (response.status === 401 || response.status === 403 || !response.ok) {
      return false;
    }
    const body = (await response.json().catch(() => null)) as { ok?: boolean } | null;
    return body?.ok === true;
  }

  /**
   * Stream a message via `POST /agent/chat/stream` (Server-Sent Events). `handlers.onToken` fires
   * for each incremental chunk so the caller can render the answer progressively; the resolved
   * {@link AgentGatewayResult} is the final, authoritative payload built from the `final` event.
   *
   * Never throws — transport failures map to a typed error result. Falls back to the caller's
   * responsibility to retry non-streaming if the stream is unavailable.
   */
  async streamMessage(request: AgentGatewayRequest, handlers: AgentGatewayStreamHandlers = {}): Promise<AgentGatewayResult> {
    const base = this.base();
    if (!base) {
      return { status: 'error', errorKind: 'unavailable' };
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), AGENT_GATEWAY_STREAM_TIMEOUT_MS);

    const headers = this.withIdempotencyKey(
      {
        ...(await this.buildHeaders(request)),
        accept: 'text/event-stream',
      },
      request,
    );
    const authErr = this.requireAuthHeaders(headers);
    if (authErr) {
      clearTimeout(timer);
      return authErr;
    }

    let response: Response;
    try {
      response = await fetch(`${base}/agent/chat/stream`, {
        method: 'POST',
        headers,
        body: this.requestBody(request),
        signal: controller.signal,
      });
    } catch (err) {
      clearTimeout(timer);
      return {
        status: 'error',
        errorKind: this.isAbort(err) ? 'timeout' : 'unavailable',
      };
    }

    if (response.status === 401 || response.status === 403) {
      clearTimeout(timer);
      return { status: 'error', errorKind: 'unauthorized' };
    }
    if (!response.ok) {
      clearTimeout(timer);
      return this.errorFromEnvelope(response);
    }
    if (!response.body) {
      clearTimeout(timer);
      return { status: 'error', errorKind: 'unavailable' };
    }

    try {
      return await this.consumeSse(response.body, handlers);
    } catch (err) {
      return {
        status: 'error',
        errorKind: this.isAbort(err) ? 'timeout' : 'unavailable',
      };
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Parse an SSE byte stream into gateway events. We act on:
   *  - `start`  → carries the conversationId (emit early so the UI can persist it)
   *  - `status` → progressive tool / working labels; `state: card` paints toggles early
   *  - `token`  → incremental answer chunk (progressive render)
   *  - `final`  → the full ChatResponseDto (authoritative result)
   * Unknown events (`done`, heartbeats) are ignored.
   */
  private async consumeSse(stream: ReadableStream<Uint8Array>, handlers: AgentGatewayStreamHandlers): Promise<AgentGatewayResult> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let answerSoFar = '';
    let finalResult: AgentGatewayResult | null = null;

    const handleEvent = (name: string, data: string): void => {
      if (name === 'start') {
        const id = this.safeParse<{ conversationId?: string }>(data)?.conversationId;
        if (id) {
          handlers.onConversationId?.(id);
        }
      } else if (name === 'status') {
        const evt = this.safeParse<AgentGatewayStatusEvent>(data);
        if (evt && typeof evt.state === 'string') {
          if (evt.state === 'card') {
            handlers.onCard?.(this.parseCardPreview(evt.card));
          }
          handlers.onStatus?.(evt);
        }
      } else if (name === 'token') {
        const chunk = this.safeParse<{ text?: string }>(data)?.text ?? '';
        if (chunk) {
          answerSoFar += chunk;
          handlers.onToken?.(chunk, answerSoFar);
        }
      } else if (name === 'final') {
        finalResult = this.toResult(this.safeParse<AgentGatewayResponseBody>(data));
      } else if (name === 'error') {
        // Gateway signalled a failure mid-stream (emits `error` then `done`, no `final`).
        // Error events carry message/errorCode/requestId/retryable for the toast.
        const err = this.safeParse<{
          message?: unknown;
          requestId?: unknown;
          retryable?: unknown;
        }>(data);
        finalResult = {
          status: 'error',
          errorKind: 'unavailable',
          ...(typeof err?.message === 'string' && err.message.trim()
            ? { message: err.message.trim() }
            : {}),
          ...(typeof err?.requestId === 'string' && err.requestId.trim()
            ? { requestId: err.requestId.trim() }
            : {}),
          ...(typeof err?.retryable === 'boolean'
            ? { retryable: err.retryable }
            : {}),
        };
      }
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line.
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        this.parseFrame(frame, handleEvent);
      }
    }
    if (buffer.trim().length > 0) {
      this.parseFrame(buffer, handleEvent);
    }

    return finalResult ?? { status: 'error', errorKind: 'malformed' };
  }

  /**
   * Parse a single SSE frame (its `event:` and possibly multi-line `data:` fields).
   * Comment lines (starting with `:`, e.g. the gateway's 15s `: ping` heartbeats)
   * fall through both branches and are ignored, per the SSE spec.
   */
  private parseFrame(frame: string, onEvent: (name: string, data: string) => void): void {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    }
    if (dataLines.length > 0) {
      onEvent(event, dataLines.join('\n'));
    }
  }

  /** Body for POST /agent/chat[/stream] — idempotencyKey travels as a header, not a body field. */
  private requestBody(request: AgentGatewayRequest): string {
    const { idempotencyKey, ...body } = request;
    void idempotencyKey;
    return JSON.stringify(body);
  }

  private withIdempotencyKey(
    headers: Record<string, string>,
    request: AgentGatewayRequest,
  ): Record<string, string> {
    if (!request.idempotencyKey?.trim()) {
      return headers;
    }
    return { ...headers, 'Idempotency-Key': request.idempotencyKey.trim() };
  }

  /**
   * Map a non-2xx response to a typed error enriched with the gateway's uniform
   * envelope: { statusCode, error, message, requestId, retryable }.
   */
  private async errorFromEnvelope(response: Response): Promise<AgentGatewayResult> {
    const envelope = (await response.json().catch(() => null)) as {
      message?: unknown;
      requestId?: unknown;
      retryable?: unknown;
    } | null;
    const message =
      typeof envelope?.message === 'string' && envelope.message.trim()
        ? envelope.message.trim()
        : undefined;
    const requestId =
      (typeof envelope?.requestId === 'string' && envelope.requestId.trim()) ||
      response.headers?.get('X-Request-Id') ||
      undefined;
    const retryable =
      typeof envelope?.retryable === 'boolean' ? envelope.retryable : undefined;
    return {
      status: 'error',
      errorKind: 'unavailable',
      ...(message ? { message } : {}),
      ...(requestId ? { requestId } : {}),
      ...(retryable !== undefined ? { retryable } : {}),
    };
  }

  private safeParse<T>(raw: string): T | null {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  /** Normalize an untrusted response body into the discriminated {@link AgentGatewayResult}. */
  private toResult(body: AgentGatewayResponseBody | null): AgentGatewayResult {
    if (!body || typeof body.answer !== 'string') {
      return { status: 'error', errorKind: 'malformed' };
    }

    const suggestedActions = Array.isArray(body.suggestedActions)
      ? body.suggestedActions.filter((a): a is string => typeof a === 'string')
      : [];
    const helpfulLinks = this.parseHelpfulLinks(body.helpfulLinks);
    const coachingSuggestions = this.parseCoachingSuggestions(body.coachingSuggestions);
    const navActions = this.parseNavActions(body.navActions);
    const reportLinks = this.parseReportLinks(body.reportLinks);
    const shortFormat = this.parseShortFormat(body.shortFormat);
    const charts = this.parseCharts(body.charts);
    const tables = this.parseTables(body.tables);
    const decision = this.parseDecision(body.decision);
    const tool = body.meta?.tool ?? null;
    const clarification = this.parseClarification(body.meta?.clarification);
    const gatewayError =
      typeof body.meta?.error === 'string' && body.meta.error.trim().length > 0
        ? body.meta.error.trim()
        : null;
    const requestId =
      (typeof body.meta?.requestId === 'string' && body.meta.requestId.trim()) ||
      (typeof body.debug?.requestId === 'string' && body.debug.requestId.trim()) ||
      undefined;

    return {
      status: 'success',
      answer: body.answer,
      suggestedActions,
      helpfulLinks,
      coachingSuggestions,
      navActions,
      reportLinks: reportLinks.length ? reportLinks : undefined,
      shortFormat,
      charts,
      tables,
      decision,
      sourceData: body.sourceData,
      unsupported: tool === null,
      tool,
      model: typeof body.meta?.model === 'string' ? body.meta.model : null,
      usage: this.parseUsage(body.meta?.usage),
      conversationId: typeof body.conversationId === 'string' ? body.conversationId : undefined,
      requestId,
      clarification,
      gatewayError,
      persona: this.parsePersona(body.persona),
      liveData: this.parseLiveDataBadge(body.liveData),
      conversationScope: this.parseConversationScope(body.conversationScope),
      peopleActions: this.parsePeopleActions(body.peopleActions),
      usageChips: this.parseUsageChips(body.usageChips),
      recommendationCard: this.parseRecommendationCard(body.recommendationCard),
      toolChecks: this.parseToolChecks(body.meta?.toolChecks),
      toolSteps: this.parseToolSteps(body.toolSteps),
      answerSource: this.parseAnswerSource(body.meta?.answerSource),
      retryable:
        typeof body.meta?.retryable === 'boolean' ? body.meta.retryable : null,
      scopeEcho:
        typeof body.meta?.scopeEcho === 'string' && body.meta.scopeEcho.trim()
          ? body.meta.scopeEcho.trim()
          : null,
      caveats: Array.isArray(body.meta?.caveats)
        ? body.meta.caveats.filter(
            (c): c is string => typeof c === 'string' && c.trim().length > 0,
          )
        : [],
    };
  }

  private parseCardPreview(
    raw: AgentGatewayStatusEvent['card'],
  ): AgentGatewayCardPreview {
    const reportLinks = this.parseReportLinks(raw?.reportLinks);
    const charts = this.parseCharts(raw?.charts);
    const tables = this.parseTables(raw?.tables);
    return {
      reportLinks: reportLinks.length ? reportLinks : undefined,
      shortFormat: this.parseShortFormat(raw?.shortFormat) ?? undefined,
      charts: charts.length ? charts : undefined,
      tables: tables.length ? tables : undefined,
      liveData: this.parseLiveDataBadge(raw?.liveData) ?? undefined,
      conversationScope: this.parseConversationScope(raw?.conversationScope) ?? undefined,
      persona: this.parsePersona(raw?.persona) ?? undefined,
    };
  }

  private parseAnswerSource(raw: unknown): AgentAnswerSource | null {
    return raw === 'llm' ||
      raw === 'llm_fallback_model' ||
      raw === 'deterministic' ||
      raw === 'error'
      ? raw
      : null;
  }

  private parseClarification(
    raw: unknown,
  ): 'date_range' | 'user' | 'team' | null {
    return raw === 'date_range' || raw === 'user' || raw === 'team' ? raw : null;
  }

  /** Keep only well-formed { label, route } nav actions — the body shape is untrusted. */
  private parseNavActions(raw: unknown): NavAction[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .filter(
        (a): a is NavAction =>
          !!a &&
          typeof (a as NavAction).label === 'string' &&
          typeof (a as NavAction).route === 'string' &&
          // Internal routes only — never navigate to an absolute/external URL.
          (a as NavAction).route.startsWith('/'),
      )
      .map((a) => ({
        label: a.label,
        route: a.route,
        params: a.params && typeof a.params === 'object' ? a.params : undefined,
        icon: typeof a.icon === 'string' ? a.icon : undefined,
      }));
  }

  private parseReportLinks(raw: unknown): ReportLink[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .filter((a): a is ReportLink => {
        const row = a as ReportLink;
        return (
          !!row &&
          typeof row.id === 'string' &&
          typeof row.label === 'string' &&
          typeof row.route === 'string' &&
          row.route.startsWith('/') &&
          Array.isArray(row.phrases)
        );
      })
      .map((a) => ({
        id: a.id,
        label: a.label,
        route: a.route,
        phrases: a.phrases.filter((p): p is string => typeof p === 'string' && !!p.trim()),
        params: a.params && typeof a.params === 'object' ? a.params : undefined,
        icon: typeof a.icon === 'string' ? a.icon : undefined,
      }));
  }

  private parseShortFormat(raw: unknown): ShortFormatCard | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const row = raw as ShortFormatCard;
    if (typeof row.disclaimer !== 'string' || !row.disclaimer.trim()) {
      return null;
    }
    const visual =
      row.visual === 'trend' || row.visual === 'people' || row.visual === 'none'
        ? row.visual
        : 'none';
    return {
      disclaimer: row.disclaimer.trim(),
      visual,
      ...(row.canShowTrend === true ? { canShowTrend: true } : {}),
      ...(row.canShowPeople === true ? { canShowPeople: true } : {}),
    };
  }

  private static readonly chartTypes = new Set<ChartType>([
    'bar',
    'line',
    'stacked_bar',
    'pie',
    'donut',
    'multi_series_bar',
    'multi_series_line',
  ]);

  /**
   * Keep only well-formed ChartSpec entries. Unknown types and bad geometry are
   * dropped (never invent series from prose).
   */
  private parseCharts(raw: unknown): ChartSpec[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    const out: ChartSpec[] = [];
    for (const item of raw) {
      if (!item || typeof item !== 'object') {
        continue;
      }
      const c = item as Partial<ChartSpec>;
      if (typeof c.id !== 'string' || !c.id.trim()) {
        continue;
      }
      if (typeof c.title !== 'string' || !c.title.trim()) {
        continue;
      }
      if (typeof c.type !== 'string' || !AgentGatewayService.chartTypes.has(c.type as ChartType)) {
        continue;
      }
      if (!Array.isArray(c.categories) || !c.categories.length) {
        continue;
      }
      if (!Array.isArray(c.series) || !c.series.length) {
        continue;
      }
      const categories = c.categories.filter((x): x is string => typeof x === 'string');
      if (categories.length !== c.categories.length || !categories.length) {
        continue;
      }
      const series = c.series
        .filter(
          (s) =>
            !!s &&
            typeof s.key === 'string' &&
            typeof s.label === 'string' &&
            Array.isArray(s.values) &&
            s.values.length === categories.length &&
            s.values.every((v) => typeof v === 'number' && Number.isFinite(v)),
        )
        .map((s) => ({
          key: s.key,
          label: s.label,
          values: s.values,
          // Pre-formatted display values must stay aligned with values[].
          formattedValues:
            Array.isArray(s.formattedValues) &&
            s.formattedValues.length === s.values.length &&
            s.formattedValues.every((f) => typeof f === 'string')
              ? s.formattedValues
              : undefined,
        }));
      if (!series.length) {
        continue;
      }
      const rawRef = (c as { referenceLine?: unknown }).referenceLine as
        | { value?: unknown, label?: unknown }
        | undefined;
      const referenceLine =
        rawRef &&
        typeof rawRef === 'object' &&
        Number.isFinite(Number(rawRef.value)) &&
        typeof rawRef.label === 'string' &&
        rawRef.label.trim()
          ? { value: Number(rawRef.value), label: rawRef.label.trim() }
          : undefined;
      out.push({
        id: c.id.trim(),
        type: c.type as ChartType,
        title: c.title.trim(),
        subtitle:
          typeof c.subtitle === 'string' && c.subtitle.trim()
            ? c.subtitle.trim()
            : undefined,
        description: typeof c.description === 'string' ? c.description : undefined,
        unit: typeof c.unit === 'string' ? c.unit : undefined,
        categories,
        series,
        referenceLine,
        sourceTool: typeof c.sourceTool === 'string' ? c.sourceTool : undefined,
      });
      if (out.length >= 2) {
        break;
      }
    }
    return out;
  }

  private static readonly tableColumnTypes = new Set<TableColumnType>([
    'text',
    'number',
    'duration',
    'percent',
  ]);

  /**
   * Keep only well-formed TableSpec entries. Unknown column types, missing
   * columns/rows, or malformed rows are dropped defensively — same posture
   * as parseCharts.
   */
  private parseTables(raw: unknown): TableSpec[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    const out: TableSpec[] = [];
    for (const item of raw) {
      if (!item || typeof item !== 'object') {
        continue;
      }
      const t = item as Partial<TableSpec>;
      if (typeof t.id !== 'string' || !t.id.trim()) {
        continue;
      }
      if (typeof t.title !== 'string' || !t.title.trim()) {
        continue;
      }
      if (!Array.isArray(t.columns) || !t.columns.length) {
        continue;
      }
      const columns: TableColumn[] = t.columns
        .filter(
          (c): c is TableColumn =>
            !!c &&
            typeof c.key === 'string' &&
            c.key.trim().length > 0 &&
            typeof c.label === 'string' &&
            typeof c.type === 'string' &&
            AgentGatewayService.tableColumnTypes.has(c.type as TableColumnType) &&
            typeof c.sortable === 'boolean',
        )
        .map((c) => ({
          key: c.key,
          label: c.label,
          type: c.type,
          sortable: c.sortable,
        }));
      if (!columns.length) {
        continue;
      }
      if (!Array.isArray(t.rows) || !t.rows.length) {
        continue;
      }
      const columnKeys = new Set(columns.map((c) => c.key));
      const rows = t.rows.filter(
        (r): r is Record<string, unknown> =>
          !!r &&
          typeof r === 'object' &&
          !Array.isArray(r) &&
          columns.every((c) => c.key in r || !columnKeys.has(c.key)),
      );
      if (!rows.length) {
        continue;
      }
      const rawSort = (t as { defaultSort?: unknown }).defaultSort as
        | { column?: unknown, direction?: unknown }
        | undefined;
      const sortColumn = rawSort?.column;
      const sortDirection = rawSort?.direction;
      let defaultSort: TableSort | undefined;
      if (
        typeof sortColumn === 'string' &&
        columnKeys.has(sortColumn) &&
        (sortDirection === 'asc' || sortDirection === 'desc')
      ) {
        defaultSort = { column: sortColumn, direction: sortDirection };
      }
      out.push({
        id: t.id.trim(),
        title: t.title.trim(),
        subtitle:
          typeof t.subtitle === 'string' && t.subtitle.trim()
            ? t.subtitle.trim()
            : undefined,
        columns,
        rows,
        defaultSort,
        sourceTool: typeof t.sourceTool === 'string' ? t.sourceTool : undefined,
      });
      if (out.length >= 2) {
        break;
      }
    }
    return out;
  }

  /** Normalize structured decision intelligence from the gateway. */
  private parseDecision(raw: unknown): DecisionResponse | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const d = raw as DecisionResponse;
    if (typeof d.observation !== 'string' || !d.confidence) {
      return null;
    }
    const level = d.confidence.level;
    if (level !== 'high' && level !== 'medium' && level !== 'low' && level !== 'insufficient') {
      return null;
    }
    return {
      observation: d.observation,
      evidence: Array.isArray(d.evidence) ? d.evidence : [],
      explanations: Array.isArray(d.explanations) ? d.explanations : [],
      confidence: d.confidence,
      recommendation: typeof d.recommendation === 'string' ? d.recommendation : '',
      recommendationType: typeof d.recommendationType === 'string' ? d.recommendationType : undefined,
      suggestedActions: Array.isArray(d.suggestedActions) ? d.suggestedActions : [],
      suggestedApproach: this.parseSuggestedApproach(
        (d as { suggestedApproach?: unknown }).suggestedApproach,
      ),
      citations: Array.isArray(d.citations) ? d.citations : [],
      signals: Array.isArray((d as { signals?: unknown }).signals)
        ? ((d as { signals: DecisionResponse['signals'] }).signals ?? [])
        : [],
      correlations: this.parseCorrelations(
        (d as { correlations?: unknown }).correlations,
      ),
    };
  }

  /** Deterministic cross-signal insight lines — the key sentence of investigation answers. */
  private parseCorrelations(raw: unknown): DecisionResponse['correlations'] {
    if (!Array.isArray(raw)) {
      return undefined;
    }
    const out = raw.filter(
      (c): c is NonNullable<DecisionResponse['correlations']>[number] =>
        !!c &&
        typeof (c as { id?: unknown }).id === 'string' &&
        typeof (c as { interpretation?: unknown }).interpretation === 'string' &&
        ((c as { severity?: unknown }).severity === 'info' ||
          (c as { severity?: unknown }).severity === 'warning'),
    );
    return out.length ? out : undefined;
  }

  private parseSuggestedApproach(raw: unknown): DecisionResponse['suggestedApproach'] {
    if (!raw || typeof raw !== 'object') {
      return undefined;
    }
    const approach = raw as DecisionResponse['suggestedApproach'];
    if (
      !approach ||
      typeof approach.title !== 'string' ||
      typeof approach.body !== 'string'
    ) {
      return undefined;
    }
    const personality = approach.personality;
    if (
      personality !== 'manager_coach' &&
      personality !== 'executive_brief' &&
      personality !== 'analyst_detailed'
    ) {
      return undefined;
    }
    return {
      title: approach.title,
      body: approach.body,
      personality,
    };
  }

  /** Normalize token usage; returns null unless all three counts are finite numbers. */
  private parseUsage(raw: unknown): TokenUsage | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const u = raw as {
      promptTokens?: unknown;
      completionTokens?: unknown;
      totalTokens?: unknown;
      credits?: unknown;
    };
    const prompt = Number(u.promptTokens);
    const completion = Number(u.completionTokens);
    const total = Number(u.totalTokens);
    if (![prompt, completion, total].every((n) => Number.isFinite(n))) {
      return null;
    }
    const credits = Number(u.credits);
    return {
      promptTokens: prompt,
      completionTokens: completion,
      totalTokens: total,
      credits: Number.isFinite(credits) ? credits : undefined,
    };
  }

  /** Keep only well-formed coaching suggestions from the gateway response. */
  private parseCoachingSuggestions(raw: unknown): CoachingSuggestion[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .filter(
        (c): c is CoachingSuggestion =>
          !!c &&
          typeof (c as CoachingSuggestion).approach === 'string' &&
          typeof (c as CoachingSuggestion).draftMessage === 'string',
      )
      .map((c) => ({
        issue: typeof c.issue === 'string' ? c.issue : 'workload_check_in',
        approach: c.approach.trim(),
        draftMessage: c.draftMessage.trim(),
        followUpActions: Array.isArray(c.followUpActions)
          ? c.followUpActions.filter((a): a is string => typeof a === 'string' && a.trim().length > 0)
          : [],
      }));
  }

  private parsePersona(raw: unknown): CopilotPersona | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const o = raw as Record<string, unknown>;
    const id = typeof o.id === 'string' ? o.id.trim() : '';
    const displayName = typeof o.displayName === 'string' ? o.displayName.trim() : '';
    if (!id || !displayName) {
      return null;
    }
    return { id, displayName };
  }

  private parseLiveDataBadge(raw: unknown): CopilotLiveDataBadge | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const o = raw as Record<string, unknown>;
    if (typeof o.active !== 'boolean') {
      return null;
    }
    const label = typeof o.label === 'string' && o.label.trim() ? o.label.trim() : 'Live data';
    return { active: o.active, label };
  }

  private parseConversationScope(raw: unknown): ConversationScope | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const o = raw as Record<string, unknown>;
    if (typeof o.narrowed !== 'boolean') {
      return null;
    }
    const scope: ConversationScope = { narrowed: o.narrowed };
    if (typeof o.label === 'string' && o.label.trim()) {
      scope.label = o.label.trim();
    }
    if (Array.isArray(o.users)) {
      scope.users = o.users
        .filter(
          (u): u is { id: string, name: string } =>
            !!u &&
            typeof (u as { id?: unknown }).id === 'string' &&
            typeof (u as { name?: unknown }).name === 'string',
        )
        .map((u) => ({ id: u.id, name: u.name }));
    }
    if (Array.isArray(o.groups)) {
      scope.groups = o.groups
        .filter(
          (g): g is { id: string, name: string } =>
            !!g &&
            typeof (g as { id?: unknown }).id === 'string' &&
            typeof (g as { name?: unknown }).name === 'string',
        )
        .map((g) => ({ id: g.id, name: g.name }));
    }
    if (o.dateRange && typeof o.dateRange === 'object') {
      const dr = o.dateRange as Record<string, unknown>;
      if (typeof dr.start === 'string' && typeof dr.end === 'string') {
        scope.dateRange = { start: dr.start, end: dr.end };
      }
    }
    if (
      o.dateRangeSource === 'message' ||
      o.dateRangeSource === 'ui' ||
      o.dateRangeSource === 'default'
    ) {
      scope.dateRangeSource = o.dateRangeSource;
    }
    if (
      typeof o.dateRangeOverrideNote === 'string' &&
      o.dateRangeOverrideNote.trim()
    ) {
      scope.dateRangeOverrideNote = o.dateRangeOverrideNote.trim();
    }
    return scope;
  }

  private parsePeopleActions(raw: unknown): PersonAction[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .filter(
        (a): a is PersonAction =>
          !!a &&
          typeof (a as PersonAction).userId === 'string' &&
          typeof (a as PersonAction).displayName === 'string' &&
          typeof (a as PersonAction).label === 'string' &&
          typeof (a as PersonAction).prompt === 'string',
      )
      .map((a) => {
        const action: PersonAction = {
          userId: a.userId.trim(),
          displayName: a.displayName.trim(),
          label: a.label.trim(),
          prompt: a.prompt.trim(),
        };
        if (
          a.nav &&
          typeof a.nav.route === 'string' &&
          a.nav.route.startsWith('/')
        ) {
          action.nav = {
            route: a.nav.route,
            params:
              a.nav.params && typeof a.nav.params === 'object'
                ? (a.nav.params )
                : undefined,
            icon: typeof a.nav.icon === 'string' ? a.nav.icon : undefined,
          };
        }
        return action;
      })
      .filter((a) => a.userId && a.label && a.prompt);
  }

  private parseUsageChips(raw: unknown): UsageChip[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .filter(
        (c): c is UsageChip =>
          !!c &&
          typeof (c as UsageChip).label === 'string' &&
          typeof (c as UsageChip).hours === 'number' &&
          Number.isFinite((c as UsageChip).hours),
      )
      .map((c) => ({
        label: c.label.trim(),
        hours: Math.round(c.hours * 10) / 10,
        ...(typeof c.pct === 'number' && Number.isFinite(c.pct) ? { pct: c.pct } : {}),
        ...(typeof c.userId === 'string' && c.userId.trim()
          ? { userId: c.userId.trim() }
          : {}),
      }))
      .filter((c) => c.label.length > 0);
  }

  private parseRecommendationCard(raw: unknown): RecommendationCard | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const o = raw as Record<string, unknown>;
    const title = typeof o.title === 'string' ? o.title.trim() : '';
    const draftMessage = typeof o.draftMessage === 'string' ? o.draftMessage.trim() : '';
    if (!title || !draftMessage || !Array.isArray(o.actions)) {
      return null;
    }
    const kinds = new Set<RecommendationActionKind>([
      'client_send_note',
      'client_skip',
      'client_ask_call',
    ]);
    const actions = o.actions
      .filter(
        (a): a is { kind: RecommendationActionKind, label: string, primary?: boolean } =>
          !!a &&
          typeof (a as { kind?: unknown }).kind === 'string' &&
          kinds.has((a as { kind: RecommendationActionKind }).kind) &&
          typeof (a as { label?: unknown }).label === 'string',
      )
      .map((a) => ({
        kind: a.kind,
        label: a.label.trim(),
        ...(a.primary === true ? { primary: true } : {}),
      }))
      .filter((a) => a.label.length > 0);
    if (!actions.length) {
      return null;
    }
    return {
      title,
      draftMessage,
      ...(typeof o.body === 'string' && o.body.trim() ? { body: o.body.trim() } : {}),
      ...(typeof o.draftLabel === 'string' && o.draftLabel.trim()
        ? { draftLabel: o.draftLabel.trim() }
        : {}),
      actions,
    };
  }

  private parseToolSteps(raw: unknown): ToolStep[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .filter(
        (step): step is ToolStep =>
          !!step &&
          typeof (step as ToolStep).text === 'string' &&
          typeof (step as ToolStep).tool === 'string',
      )
      .map((step) => ({
        text: step.text.trim(),
        tool: step.tool.trim(),
        done: true,
      }))
      .filter((step) => step.text && step.tool);
  }

  private parseToolChecks(raw: unknown): ToolCheck[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .filter(
        (c): c is ToolCheck =>
          !!c &&
          typeof (c as ToolCheck).id === 'string' &&
          typeof (c as ToolCheck).label === 'string' &&
          typeof (c as ToolCheck).simulated === 'boolean',
      )
      .map((c) => ({
        id: c.id.trim(),
        label: c.label.trim(),
        simulated: c.simulated,
      }))
      .filter((c) => c.id && c.label);
  }

  /** Keep only well-formed { title, url } entries — the body shape is untrusted. */
  private parseHelpfulLinks(raw: unknown): HelpLink[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .filter(
        (l): l is HelpLink =>
          !!l &&
          typeof (l as HelpLink).title === 'string' &&
          typeof (l as HelpLink).url === 'string' &&
          /^https?:\/\//i.test((l as HelpLink).url),
      )
      .map((l) => ({ title: l.title, url: l.url }));
  }

  /**
   * Base transport headers plus the session's auth + identity headers.
   *
   * Cookies are intentionally NOT sent (`credentials` omitted): the gateway authenticates by
   * the forwarded `Authorization` header, and its CORS does not allow credentialed
   * cross-origin requests. Auth headers come from {@link AGENT_GATEWAY_AUTH} — see
   * `agent-gateway-auth.provider.ts` for the token strategy.
   *
   * When a personal MCP key has been provisioned, {@link AGENT_GATEWAY_AUTH} forwards it as
   * `x-td-mcp-key` so the gateway can read live workspace data via MCP.
   */
  private async buildHeaders(request: AgentGatewayRequest): Promise<Record<string, string>> {
    let authHeaders = await this.auth.getAuthHeaders(request.context);
    // Profile token can lag AuthState hydration; one more pass before sending.
    if (!this.hasAuthorization(authHeaders)) {
      await new Promise((r) => setTimeout(r, 300));
      authHeaders = await this.auth.getAuthHeaders(request.context);
    }
    return {
      'content-type': 'application/json',
      accept: 'application/json',
      ...authHeaders,
    };
  }

  private hasAuthorization(headers: Record<string, string>): boolean {
    return Object.keys(headers).some(
      (k) => k.toLowerCase() === 'authorization' && !!headers[k]?.trim(),
    );
  }

  /**
   * Chat/stream require a session bearer. Avoid hitting the gateway without one
   * (maps to a confusing "session expired" UX when the user is already logged in).
   */
  private requireAuthHeaders(
    headers: Record<string, string>,
  ): AgentGatewayResult | null {
    if (this.hasAuthorization(headers)) {
      return null;
    }
    return { status: 'error', errorKind: 'unauthorized' };
  }

  private base(): string {
    return (environment.agentGatewayBaseUrl || '').replace(/\/+$/, '');
  }

  private isAbort(err: unknown): boolean {
    return err instanceof DOMException ? err.name === 'AbortError' : (err as { name?: string })?.name === 'AbortError';
  }
}
